// Follow (named Discover until 29 Jul 2026; the word moved to the
// practices screen): the full roster as a grid, full width, three columns
// (the rail cards were cut 28 Jul 2026; the room belongs to people). The
// whole card opens the profile; Follow stops propagation so following never
// navigates away from the grid. Recommended is the default filter: ordered
// by the user's areas and topic switches, the same ranking onboarding uses.
import type { Descriptor } from "../shell/descriptor.js";
import { Avatar } from "../shell/Avatar.js";
import { roleLine } from "../shell/fragments.js";
import { SearchBox } from "../shell/SurfaceSearch.js";
import type { ExpertLite } from "../state/api.js";
import { catsForAreas, recommendSort } from "../state/recommend.js";
import { useStore, type AppStore } from "../state/store.js";

function ExpertCard({ e }: { e: ExpertLite }) {
  const s = useStore();
  const following = s.appState?.follows.includes(e.id) ?? false;
  const open = () => s.nav("expert", e.id);
  return (
    <div
      className="ecard"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          open();
        }
      }}
    >
      <div className="top">
        <Avatar e={e} size="m" />
        <span className="grow">
          <strong>{e.name}</strong>
          <small>{roleLine(e)}</small>
        </span>
        <button
          className={`btn ${following ? "quiet unfl" : ""}`}
          onClick={(ev) => {
            ev.stopPropagation();
            s.toggleFollow(e.id);
          }}
        >
          {/* Pointing at Following says what the click will do (29 Jul
              2026): the resting label states the fact, hover and focus
              state the action. */}
          {following ? (
            <>
              <span className="stay">Following</span>
              <span className="go">Unfollow</span>
            </>
          ) : (
            "Follow"
          )}
        </button>
      </div>
      <div className="st">
        {(e.cats?.length ? e.cats : e.cat ? [e.cat] : []).join(" · ")}
        {" · "}
        {e.cardCount > 0
          ? `${e.cardCount} practice${e.cardCount === 1 ? "" : "s"}`
          : "Research queued"}
      </div>
      {/* No topic pills under the lens (29 Jul 2026): the category row
          above already says what the person covers, and the two lists
          read as the same thing said twice. Topics still do their real
          work on the switches and the per-expert controls screen. */}
      {e.lens && <div className="blurb">{e.lens}</div>}
    </div>
  );
}

export function follow(state: AppStore): Descriptor {
  const all = state.experts ?? [];
  const follows = new Set(state.appState?.follows ?? []);
  // Chips come from every category an expert sits in, not just the primary:
  // one person can be in several (28 Jul 2026).
  const cats = [...new Set(all.flatMap((e) => (e.cats?.length ? e.cats : e.cat ? [e.cat] : [])))].sort();

  const areaCatSet = catsForAreas(state.appState?.areas ?? []);
  const onTopics = new Set((state.appState?.topics ?? []).filter((t) => t.on).map((t) => t.id));

  // For You is a lane, not an ordering (31 Jul 2026, Rob: its count matched
  // All's because it held the whole roster and only sorted it). It holds the
  // people your chosen areas point at; All is everybody. With no areas on
  // record there is nothing to point with, so it falls back to everyone
  // rather than showing an empty screen.
  const forYou = (e: ExpertLite) => {
    if (areaCatSet.size === 0) return true;
    const ecats = e.cats?.length ? e.cats : e.cat ? [e.cat] : [];
    return ecats.some((c) => areaCatSet.has(c));
  };

  const q = state.followQ.trim().toLowerCase();
  const filtered = all.filter((e) => {
    const ecats = e.cats?.length ? e.cats : e.cat ? [e.cat] : [];
    if (state.followCat === "following" && !follows.has(e.id)) return false;
    if (state.followCat === "recommended" && !forYou(e)) return false;
    if (!["all", "following", "recommended"].includes(state.followCat) && !ecats.includes(state.followCat)) return false;
    if (!q) return true;
    return `${e.name} ${e.lens ?? ""} ${e.role ?? ""} ${ecats.join(" ")}`.toLowerCase().includes(q);
  });
  const list =
    state.followCat === "recommended"
      ? recommendSort(filtered, areaCatSet, (id) => onTopics.has(id))
      : filtered;

  // The toolbar row plus the seg filter bar, same shape as every other
  // surface (29 Jul 2026). No source select: this whole screen is sources.
  const bar = (
    <div className="searchbar">
      <SearchBox
        value={state.followQ}
        onChange={(q) => state.setFollowQ(q)}
        placeholder="Search names, topics, lenses…"
        label="Search the roster"
      />
    </div>
  );

  const catCount = (c: string) =>
    all.filter((e) => (e.cats?.length ? e.cats : e.cat ? [e.cat] : []).includes(c)).length;

  const main = list.length ? (
    <div className="grid3">
      {list.map((e) => (
        <ExpertCard key={e.id} e={e} />
      ))}
    </div>
  ) : state.followCat === "following" ? (
    <p className="empty">You are not following anyone yet.</p>
  ) : (
    <p className="empty">Nobody matches. Try fewer words.</p>
  );

  return {
    nav: "follow",
    title: "Follow",
    bar,
    tabs: {
      items: [
        // Following before All (31 Jul 2026, Rob): the two lanes about you
        // sit together, and the whole roster is the one you go looking for.
        ["recommended", "For You"],
        ["following", "Following"],
        ["all", "All"],
        ...cats.map((c): [string, string] => [c, c]),
      ],
      counts: {
        recommended: all.filter(forYou).length,
        all: all.length,
        following: follows.size,
        ...Object.fromEntries(cats.map((c) => [c, catCount(c)])),
      },
      active: state.followCat,
      onPick: (c) => state.setFollowCat(c),
    },
    main,
  };
}
